import { memo, useEffect, useState } from "react";
import Select from "react-select";
import { useGetAreasQuery } from "../../../features/staticDataSlice/staticDataSlice";
import toast from "react-hot-toast";
import { useDebounce } from "../../../hooks/useDebounce";
import "./Selectors.css";

const AreaSelect = ({
  label,
  value,
  onChange,
  isDisabled = false,
  placeholder = "Select",
}) => {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [pageSize] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const [options, setOptions] = useState([]);
  const debounceSearch = useDebounce(query);

  const {
    data: areasData,
    error: areasError,
    isFetching,
  } = useGetAreasQuery({
    query: debounceSearch,
    key: "",
    page,
    pageSize,
  });

  useEffect(() => {
    if (areasData) {
      const { items } = areasData;
      const newOptions = items?.map((area) => ({
        label: `${area.key} - ${area.description}`,
        value: area.id,
      }));

      setOptions((prevOptions) => {
        const seen = new Set();
        const mergedOptions = [
          ...prevOptions,
          ...newOptions,
          ...(value ? [value] : []),
        ].filter((opt) => {
          if (seen.has(opt.value)) return false;
          seen.add(opt.value);
          return true;
        });

        return mergedOptions;
      });

      setHasMore(items?.length >= pageSize);
    }

    if (areasError) {
      toast.error(areasError?.data?.Message || "Failed to load");
    }
  }, [areasData, areasError, pageSize, value]);

  const handleMenuScrollToBottom = () => {
    if (hasMore && !isFetching) {
      setPage((prev) => prev + 1);
    }
  };

  const selectedOption =
    options.find((opt) =>
      typeof value === "object" && value !== null
        ? opt.value === value.value
        : opt.value === value
    ) || null;

  const handleSearch = (inputValue) => {
    setQuery(inputValue);
    setPage(1);
    if (inputValue) {
      setOptions([]);
    }
  };

  const handleChange = (selected) => {
    onChange(selected || null);
    if (!selected) {
      handleSearch("");
    }
  };

  return (
    <>
      {label && <label className="custom-label">{label}</label>}
      <Select
        className="paginated-selector"
        classNamePrefix="custom-select"
        options={options}
        value={selectedOption}
        isDisabled={isDisabled}
        isClearable={true}
        isLoading={isFetching}
        onChange={handleChange}
        onMenuScrollToBottom={handleMenuScrollToBottom}
        onInputChange={(inputValue, { action }) => {
          if (action === "input-change") {
            handleSearch(inputValue);
          }
        }}
        placeholder={placeholder}
      />
    </>
  );
};

export default memo(AreaSelect);
